/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector } from 'react-redux';
import { renderCounter } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import { layerSelectors } from '@opengeoweb/store';
import { LayerType } from '@opengeoweb/webmap';
import { AcceptanceTimeConnect } from './AcceptanceTime/AcceptanceTimeConnect';
import ActivateLayerConnect from './ActivateLayer/ActivateLayerConnect';
import CustomDimensionSelectConnect from './CustomDimensionSelect/CustomDimensionSelectConnect';
import DeleteLayerConnect from './DeleteLayer/DeleteLayerConnect';
import DimensionSelectConnect from './DimensionSelect/DimensionSelectConnect';
import EnableLayerConnect from './EnableLayer/EnableLayerConnect';
import LayerRow from './LayerRow';
import LayerManagerMenuButtonConnect from './Menubutton/MenuButtonConnect';
import { MissingDataConnect } from './MissingData/MissingDataConnect';
import OpacityLayerConnect from './OpacitySelect/OpacitySelectConnect';
import RenderLayersConnect from './RenderLayers/RenderLayersConnect';
import RenderStylesConnect from './RenderStyles/RenderStylesConnect';
import { LayerInfoButtonConnect } from '../../../LayerInfoConnect';
import type { LayerManagerCustomSettings } from '../../LayerManagerUtils';

interface LayerRowConnectProps {
  layerId: string;
  mapId: string;
  dragHandle: React.ReactElement;
  settings?: LayerManagerCustomSettings['content'];
  collapsedColumns?: Record<string, boolean>;
}

// Add a type for dragHandle.props to avoid 'unknown' error
interface DragHandleProps {
  isDisabled?: boolean;
  toolTipTitle?: string;
  [key: string]: unknown;
}

// Whenever a new layer is added index is updated in the dragHandle, which modifies the testing text. Below we ignore this text, as it serves no other purpose.
const areEqual = (
  prevProps: LayerRowConnectProps,
  nextProps: LayerRowConnectProps,
): boolean => {
  const prevDragHandleProps = prevProps.dragHandle.props as DragHandleProps;
  const nextDragHandleProps = nextProps.dragHandle.props as DragHandleProps;

  if (
    prevProps.layerId === nextProps.layerId &&
    prevProps.mapId === nextProps.mapId &&
    prevProps.settings === nextProps.settings &&
    prevDragHandleProps.isDisabled === nextDragHandleProps.isDisabled &&
    prevDragHandleProps.toolTipTitle === nextDragHandleProps.toolTipTitle &&
    prevProps.collapsedColumns === nextProps.collapsedColumns
  ) {
    return true;
  }
  return false;
};

const LayerRowConnect: React.FC<LayerRowConnectProps> = React.memo(
  ({
    layerId,
    mapId,
    dragHandle,
    settings,
    collapsedColumns,
  }: LayerRowConnectProps) => {
    const isEnabled = useSelector((store: CoreAppStore) =>
      layerSelectors.getLayerEnabled(store, layerId),
    );
    const layerName = useSelector((store: CoreAppStore) =>
      layerSelectors.getLayerName(store, layerId),
    );
    const layerDisplayName = useSelector((store: CoreAppStore) =>
      layerSelectors.getLayerDisplayName(store, layerId),
    );
    const layerServiceName = useSelector((store: CoreAppStore) =>
      layerSelectors.getLayerServiceName(store, layerId),
    );

    const layerType = useSelector((store: CoreAppStore) =>
      layerSelectors.getLayerType(store, layerId),
    );
    const isEDRLayer = layerType === LayerType.edrFeatureLayer;
    const isFeatureLayer = layerType === LayerType.featureLayer;
    const isStacGeoTIFFLayer = layerType === LayerType.stacGeoTIFFLayer;
    const layerServiceUrl = useSelector((store: CoreAppStore) =>
      layerSelectors.getLayerService(store, layerId),
    );
    const isLayerMissing = useSelector((store: CoreAppStore) =>
      !isFeatureLayer && !isStacGeoTIFFLayer
        ? layerSelectors.getIsLayerMissing(store, layerId)
        : false,
    );

    renderCounter.count(LayerRowConnect.name);
    return (
      <LayerRow
        collapsedColumns={collapsedColumns}
        disableActivateLayer={settings?.activateLayer?.isDisabled}
        dragHandle={dragHandle}
        isEDRLayer={isEDRLayer}
        isEnabled={isEnabled}
        isLayerMissing={isLayerMissing}
        layerAcceptanceTimeLayout={
          <AcceptanceTimeConnect
            layerId={layerId}
            mapId={mapId}
            skipLocalStyling={true}
            {...settings?.acceptanceTime}
          />
        }
        layerActiveLayout={
          settings?.activateLayer?.isDisabled ? undefined : (
            <ActivateLayerConnect layerId={layerId} mapId={mapId} />
          )
        }
        layerDeleteLayout={
          <DeleteLayerConnect
            layerId={layerId}
            mapId={mapId}
            {...settings?.deleteLayer}
          />
        }
        layerDimensionLayout={
          settings?.dimensions ? (
            <CustomDimensionSelectConnect
              layerId={layerId}
              mapId={mapId}
              {...settings?.dimensions}
            />
          ) : (
            <DimensionSelectConnect
              layerId={layerId}
              mapId={mapId}
              skipLocalStyling={true}
            />
          )
        }
        layerEnableLayout={
          <EnableLayerConnect
            icon={
              isEnabled
                ? settings?.enableLayer?.enabledIcon
                : settings?.enableLayer?.disabledIcon
            }
            isEnabled={isEnabled}
            layerId={layerId}
            layerName={layerDisplayName}
            mapId={mapId}
            tooltipTitle={
              isEnabled
                ? settings?.enableLayer?.enabledTooltipTitle
                : settings?.enableLayer?.disabledTooltipTitle
            }
          />
        }
        layerId={layerId}
        layerMenuLayout={
          <LayerManagerMenuButtonConnect
            layerId={layerId}
            mapId={mapId}
            {...settings?.menu}
          />
        }
        layerMissingDataLayout={
          <MissingDataConnect layerId={layerId} mapId={mapId} />
        }
        layerName={layerDisplayName}
        layerOpacityLayout={
          <OpacityLayerConnect
            layerId={layerId}
            mapId={mapId}
            tooltipPrefix={settings?.opacity?.tooltipPrefix}
          />
        }
        layerServiceName={layerServiceName}
        layerServicesLayout={
          settings?.renderLayer?.Element ? (
            <settings.renderLayer.Element layerId={layerId} mapId={mapId} />
          ) : (
            <RenderLayersConnect
              layerId={layerId}
              mapId={mapId}
              skipLocalStyling={true}
              tooltipPrefix={settings?.renderLayer?.tooltipPrefix}
            />
          )
        }
        layerShowLayerInfoLayout={
          <LayerInfoButtonConnect
            isFeatureLayer={isFeatureLayer}
            isLayerMissing={isLayerMissing}
            layerId={layerId}
            layerName={layerName}
            mapId={mapId}
            serviceUrl={layerServiceUrl}
          />
        }
        layerStylesLayout={
          <RenderStylesConnect
            layerId={layerId}
            mapId={mapId}
            skipLocalStyling={true}
            {...settings?.layerStyle}
          />
        }
        mapId={mapId}
      />
    );
  },
  areEqual,
);

export default LayerRowConnect;
